import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, TrendingDown, Hourglass, BarChart2, Repeat, Crown } from 'lucide-react';
import {
  fetchBalance,
  fetchUsage,
  fetchAutoTopup,
  fetchPacks,
  fetchTiers,
  fetchMyTier,
  updateAutoTopup,
  type CreditBalance,
  type UsageAnalytics,
  type AutoTopupSettings,
  type CreditPack,
  type SubscriptionTier,
} from '@/lib/billing';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BuyCreditsModal } from '@/components/BuyCreditsModal';

const PERIOD_CHOICES = [7, 30, 90] as const;

const CATEGORY_LABELS: Record<string, string> = {
  voice: 'Voice',
  text: 'Text',
  workflow: 'Workflow',
  agent: 'Agent',
  unknown: 'Other',
};

/**
 * Story 50.5 — per-tenant credit usage analytics. Balance + buy-credits
 * + daily burn bar chart + category breakdown + runway estimate.
 */
export function BillingPage() {
  const { t } = useTranslation();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [usage, setUsage] = useState<UsageAnalytics | null>(null);
  const [days, setDays] = useState<typeof PERIOD_CHOICES[number]>(30);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchBalance().catch(() => null),
      fetchUsage(days).catch(() => null),
    ])
      .then(([b, u]) => {
        setBalance(b);
        setUsage(u);
      })
      .finally(() => setLoading(false));
  }, [days, refreshToken]);

  if (loading && !usage) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  const maxDaily = Math.max(
    1,
    ...(usage?.daily.map((d) => Math.max(d.debits, d.credits)) ?? []),
  );
  const maxCategory = Math.max(1, ...(usage?.byCategory.map((c) => c.debits) ?? []));
  const totalDebits = usage?.daily.reduce((s, d) => s + d.debits, 0) ?? 0;
  const totalCredits = usage?.daily.reduce((s, d) => s + d.credits, 0) ?? 0;

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Coins className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">{t('billing.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('billing.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {PERIOD_CHOICES.map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
            >
              {t('billing.period_days', { days: d, defaultValue: '{{days}}d' })}
            </Button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('billing.balance_label')}
          </div>
          <div className="text-3xl font-bold">
            {(balance?.balance ?? 0).toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('billing.low_balance_threshold', {
              threshold: balance?.lowBalanceThreshold ?? 0,
              defaultValue: 'Low balance alert at {{threshold}}',
            })}
          </div>
          <Button size="sm" onClick={() => setBuyOpen(true)}>
            {t('billing.buy_credits')}
          </Button>
        </Card>

        <Card className="space-y-2">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('billing.spent_window', { days, defaultValue: 'Spent in last {{days}}d' })}
            </span>
          </div>
          <div className="text-3xl font-bold">{totalDebits.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">
            {t('billing.topped_up', {
              credits: totalCredits.toLocaleString(),
              defaultValue: 'Topped up: {{credits}}',
            })}
          </div>
        </Card>

        <Card className="space-y-2">
          <div className="flex items-center gap-2">
            <Hourglass className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('billing.runway_label')}
            </span>
          </div>
          <div className="text-3xl font-bold">
            {usage?.estimatedRunwayDays !== null && usage?.estimatedRunwayDays !== undefined
              ? `${usage.estimatedRunwayDays}d`
              : '—'}
          </div>
          <div className="text-xs text-muted-foreground">
            {usage?.estimatedRunwayDays !== null && usage?.estimatedRunwayDays !== undefined
              ? t('billing.runway_explainer', {
                  defaultValue: 'At the current burn rate, the balance lasts this many days.',
                })
              : t('billing.runway_unknown', {
                  defaultValue: 'Not enough activity to estimate yet.',
                })}
          </div>
        </Card>
      </div>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('billing.daily_burn')}
          </h2>
        </div>
        {usage && usage.daily.length > 0 ? (
          <div className="flex h-32 items-end gap-px overflow-x-auto">
            {usage.daily.map((d) => {
              const debitH = (d.debits / maxDaily) * 90;
              const creditH = (d.credits / maxDaily) * 90;
              return (
                <div
                  key={d.date}
                  className="group relative flex w-3 flex-col items-stretch justify-end"
                  title={`${d.date}: -${d.debits} / +${d.credits}`}
                >
                  {d.credits > 0 && (
                    <div
                      className="bg-emerald-500/40"
                      style={{ height: `${Math.max(creditH, 1)}%` }}
                    />
                  )}
                  <div
                    className="bg-rose-500/70"
                    style={{ height: `${Math.max(debitH, 1)}%` }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('billing.no_activity')}</p>
        )}
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>{usage?.daily[0]?.date ?? '—'}</span>
          <span>{usage?.daily[usage.daily.length - 1]?.date ?? '—'}</span>
        </div>
      </Card>

      <Card>
        <div className="mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('billing.by_category')}
          </h2>
        </div>
        {usage && usage.byCategory.length > 0 ? (
          <div className="space-y-2">
            {usage.byCategory.map((c) => {
              const pct = (c.debits / maxCategory) * 100;
              return (
                <div key={c.category}>
                  <div className="mb-1 flex justify-between text-xs">
                    <span className="font-medium">{CATEGORY_LABELS[c.category] ?? c.category}</span>
                    <span className="font-mono text-muted-foreground">
                      {c.debits.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded bg-muted">
                    <div
                      className="h-2 rounded bg-rose-500/70"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('billing.no_activity')}</p>
        )}
      </Card>

      <TierSection refreshToken={refreshToken} />

      <AutoTopupCard refreshToken={refreshToken} onSaved={() => setRefreshToken((x) => x + 1)} />

      <BuyCreditsModal
        open={buyOpen}
        onClose={() => {
          setBuyOpen(false);
          setRefreshToken((x) => x + 1);
        }}
      />
    </div>
  );
}

function TierSection({ refreshToken }: { refreshToken: number }) {
  const { t } = useTranslation();
  const [mine, setMine] = useState<SubscriptionTier | null>(null);
  const [all, setAll] = useState<SubscriptionTier[]>([]);

  useEffect(() => {
    Promise.all([fetchMyTier().catch(() => null), fetchTiers().catch(() => [])]).then(
      ([m, a]) => {
        setMine(m);
        setAll(a);
      },
    );
  }, [refreshToken]);

  if (!mine) return null;

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        <Crown className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('billing.tier_title')}
        </h2>
      </div>
      <div className="rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2">
        <div className="text-xs uppercase text-primary">{t('billing.tier_current')}</div>
        <div className="mt-1 text-lg font-bold">{mine.name}</div>
        {mine.description && (
          <div className="mt-1 text-xs text-muted-foreground">{mine.description}</div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          <Entitlement label={t('billing.tier_credits')} value={mine.bundledCreditsMonthly.toLocaleString()} />
          <Entitlement label={t('billing.tier_users')} value={mine.maxUsers?.toString() ?? '∞'} />
          <Entitlement label={t('billing.tier_agents')} value={mine.maxAgents?.toString() ?? '∞'} />
          <Entitlement label={t('billing.tier_workflows')} value={mine.maxConcurrentWorkflows?.toString() ?? '∞'} />
        </div>
      </div>
      {all.length > 1 && (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {t('billing.tier_compare', { defaultValue: 'Compare all tiers' })}
          </summary>
          <table className="mt-2 w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1">{t('billing.tier_name')}</th>
                <th className="px-2 py-1 text-right">{t('billing.tier_price')}</th>
                <th className="px-2 py-1 text-right">{t('billing.tier_credits')}</th>
                <th className="px-2 py-1 text-right">{t('billing.tier_users')}</th>
                <th className="px-2 py-1 text-right">{t('billing.tier_agents')}</th>
                <th className="px-2 py-1 text-right">{t('billing.tier_workflows')}</th>
              </tr>
            </thead>
            <tbody>
              {all.map((tier) => (
                <tr
                  key={tier.code}
                  className={`border-t ${tier.code === mine.code ? 'bg-primary/5 font-medium' : ''}`}
                >
                  <td className="px-2 py-1">{tier.name}</td>
                  <td className="px-2 py-1 text-right">
                    {tier.monthlyPriceCents
                      ? `$${(tier.monthlyPriceCents / 100).toFixed(0)}/mo`
                      : '—'}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {tier.bundledCreditsMonthly.toLocaleString()}
                  </td>
                  <td className="px-2 py-1 text-right">{tier.maxUsers ?? '∞'}</td>
                  <td className="px-2 py-1 text-right">{tier.maxAgents ?? '∞'}</td>
                  <td className="px-2 py-1 text-right">{tier.maxConcurrentWorkflows ?? '∞'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Card>
  );
}

function Entitlement({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function AutoTopupCard({
  refreshToken,
  onSaved,
}: {
  refreshToken: number;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AutoTopupSettings | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [packId, setPackId] = useState<string>('');
  const [threshold, setThreshold] = useState('100');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchAutoTopup().catch(() => null), fetchPacks().catch(() => [])])
      .then(([s, p]) => {
        if (s) {
          setSettings(s);
          setEnabled(s.enabled);
          setPackId(s.packId ?? '');
          setThreshold(String(s.lowBalanceThreshold));
        }
        setPacks(p);
      });
  }, [refreshToken]);

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateAutoTopup({
        enabled,
        packId: enabled ? packId || null : null,
        lowBalanceThreshold: parseInt(threshold) || 100,
      });
      setSettings(updated);
      onSaved();
    } catch {
      setError(t('billing.autotopup_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        <Repeat className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('billing.autotopup_title')}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('billing.autotopup_subtitle')}</p>
      {settings && !settings.paymentMethodOnFile && (
        <div className="rounded-md border border-amber-800 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
          {t('billing.autotopup_no_pm')}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4"
        />
        {t('billing.autotopup_enable')}
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-medium">{t('billing.autotopup_pack')}</label>
          <select
            value={packId}
            onChange={(e) => setPackId(e.target.value)}
            disabled={!enabled}
            className="w-full rounded-md border bg-transparent px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">{t('billing.autotopup_pick_pack')}</option>
            {packs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.credits.toLocaleString()} cr
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">{t('billing.autotopup_threshold')}</label>
          <Input
            type="number"
            min="0"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={submitting}>
          {submitting ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </Card>
  );
}
